import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core'
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms'
import { MatInput } from '@angular/material/input'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { AuctionFacade } from 'libs/gb-front/auction/data-access/src'
import { AuthFacade, IdentityTypeEnum } from 'libs/gb-front/auth/data-access/src'
import * as AuthActions from 'libs/gb-front/auth/data-access/src/lib/+state/auth.actions'
import { GbCustomValidators } from 'libs/shared/utils/helpers/src/lib/form-validators/custom-form.validator'
import { distinctUntilChanged, Subscription } from 'rxjs'

@UntilDestroy()
@Component({
  selector: 'gb-login-form',
  templateUrl: './gb-login-form.component.html',
  styleUrls: ['./gb-login-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GbLoginFormComponent implements OnInit, OnDestroy {
  @Input() redirectHome = false
  @Input() dialog = false

  @Output() cancel = new EventEmitter<void>()

  @ViewChild(MatInput) matInput?: MatInput

  auction$ = this.auctionFacade.auction$

  loginForm: FormGroup = new FormGroup({
    identity: new FormControl('', [Validators.required, Validators.minLength(5)]),
    password: new FormControl('', [Validators.required]),
  })

  identityType: IdentityTypeEnum = IdentityTypeEnum.Email
  isBothEmailMobileAllowed = false
  identityControlSubscription?: Subscription

  get isEmailType(): boolean {
    return this.identityType === IdentityTypeEnum.Email
  }

  get isMobileType(): boolean {
    return this.identityType === IdentityTypeEnum.Mobile
  }

  get identityControl(): AbstractControl {
    return this.loginForm.get('identity')!
  }

  constructor(
    private authFacade: AuthFacade,
    private auctionFacade: AuctionFacade,
    private _changeDetectorRef: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.auctionFacade.identityType$.pipe(untilDestroyed(this), distinctUntilChanged()).subscribe((identityType) => {
      this.isBothEmailMobileAllowed = identityType === IdentityTypeEnum.Both
      this.setIdentityType(identityType)
      if (this.isBothEmailMobileAllowed) {
        this.watchIdentityChanges()
      }
    })
  }

  ngOnDestroy(): void {
    this.identityControlSubscription?.unsubscribe()
  }

  watchIdentityChanges(): void {
    this.identityControlSubscription = this.identityControl.valueChanges.subscribe((value) =>
      this.setIdentityTypeOnValueChanges(value),
    )
  }

  setIdentityTypeOnValueChanges(value: string): void {
    // allow numbers, hyphens and plus sign
    const pattern = /^(?=.*[0-9])[- +()0-9]+$/

    if (value.length > 5 && pattern.test(value) && this.isEmailType) {
      this.setIdentityType(IdentityTypeEnum.Mobile)
    } else if (!pattern.test(value) && this.isMobileType) {
      this.setIdentityType(IdentityTypeEnum.Email)
      this._changeDetectorRef.detectChanges()
      this.matInput?.focus()
    }
  }

  setIdentityType(identityType: IdentityTypeEnum): void {
    this.identityType = identityType === IdentityTypeEnum.Both ? IdentityTypeEnum.Email : identityType
    this.setIdentityValidator()
  }

  setIdentityValidator(): void {
    this.isMobileType ? this.setPhoneValidator() : this.setEmailValidator()
  }

  setEmailValidator(): void {
    this.identityControl.removeValidators(GbCustomValidators.mobileNumberValidator)
    this.identityControl.addValidators(Validators.email)
  }

  setPhoneValidator(): void {
    this.identityControl.removeValidators(Validators.email)
    this.identityControl.addValidators(GbCustomValidators.mobileNumberValidator)
  }

  getIdentityInputLabel(): string {
    if (this.isBothEmailMobileAllowed) {
      return 'AUTH.LOGIN.IDENTITY.BOTH'
    }
    return this.isMobileType ? 'AUTH.LOGIN.IDENTITY.PHONE' : 'AUTH.LOGIN.IDENTITY.EMAIL'
  }

  onSubmit(): void {
    this.authFacade.dispatch(
      AuthActions.login({
        identity: this.identityControl.value,
        password: this.loginForm.get('password')!.value,
        redirectHome: this.redirectHome,
        dialog: this.dialog,
      }),
    )
  }
}
